import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Share2, Mail, Link as LinkIcon, Check } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

// Minimal brand marks (lucide has no brand icons).
const Icon = {
  whatsapp: (p) => <svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M17.5 14.4c-.3-.2-1.7-.9-2-1-.3-.1-.5-.2-.6.2s-.7 1-.9 1.1c-.2.2-.3.2-.6.1a8 8 0 0 1-2.4-1.5 9 9 0 0 1-1.6-2c-.2-.4 0-.5.1-.7l.5-.5c.1-.2.2-.3.3-.5s0-.4 0-.5l-.9-2.1c-.2-.5-.4-.5-.6-.5h-.5c-.2 0-.5 0-.7.3s-1 1-1 2.4 1 2.9 1.2 3.1c.1.2 2 3 4.7 4.2.7.3 1.2.5 1.6.6.7.2 1.3.2 1.8.1.5-.1 1.7-.7 2-1.4.2-.7.2-1.2.1-1.4-.1-.1-.3-.2-.6-.3zM12 2a10 10 0 0 0-8.6 15L2 22l5.2-1.4A10 10 0 1 0 12 2z"/></svg>,
  facebook: (p) => <svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.3v7A10 10 0 0 0 22 12z"/></svg>,
  x: (p) => <svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M18.9 2H22l-7.3 8.3L23 22h-6.8l-5.3-6.9L4.8 22H1.7l7.8-8.9L1 2h7l4.8 6.3zm-2.4 18h1.7L7.6 3.8H5.8z"/></svg>,
  telegram: (p) => <svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M22 3 2.5 10.5c-1 .4-1 1.5 0 1.8l4.9 1.5 1.9 5.9c.3.8 1 .9 1.6.4l2.7-2.4 4.8 3.5c.7.5 1.5.2 1.7-.7L23 4.4c.2-1-.5-1.7-1-1.4zM9.6 14.2l8.4-5.3c.3-.2.6.2.4.4L11.7 16c-.2.2-.3.4-.3.7l-.3 2.4z"/></svg>,
  instagram: (p) => <svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M12 2c2.7 0 3 0 4.1.1 1 0 1.7.2 2.3.5.6.2 1.1.5 1.6 1s.8 1 1 1.6c.3.6.4 1.3.5 2.3.1 1.1.1 1.4.1 4.1s0 3-.1 4.1c0 1-.2 1.7-.5 2.3-.2.6-.5 1.1-1 1.6s-1 .8-1.6 1c-.6.3-1.3.4-2.3.5-1.1.1-1.4.1-4.1.1s-3 0-4.1-.1c-1 0-1.7-.2-2.3-.5a4.3 4.3 0 0 1-1.6-1c-.5-.5-.8-1-1-1.6-.3-.6-.4-1.3-.5-2.3C2 15 2 14.7 2 12s0-3 .1-4.1c0-1 .2-1.7.5-2.3.2-.6.5-1.1 1-1.6s1-.8 1.6-1c.6-.3 1.3-.4 2.3-.5C9 2 9.3 2 12 2zm0 1.8c-2.7 0-3 0-4 .1-.8 0-1.2.2-1.5.3-.4.1-.7.3-1 .6s-.5.6-.6 1c-.1.3-.3.7-.3 1.5-.1 1-.1 1.3-.1 4s0 3 .1 4c0 .8.2 1.2.3 1.5.1.4.3.7.6 1s.6.5 1 .6c.3.1.7.3 1.5.3 1 .1 1.3.1 4 .1s3 0 4-.1c.8 0 1.2-.2 1.5-.3.4-.1.7-.3 1-.6s.5-.6.6-1c.1-.3.3-.7.3-1.5.1-1 .1-1.3.1-4s0-3-.1-4c0-.8-.2-1.2-.3-1.5a2.7 2.7 0 0 0-.6-1c-.3-.3-.6-.5-1-.6-.3-.1-.7-.3-1.5-.3-1-.1-1.3-.1-4-.1zm0 3.1a5.1 5.1 0 1 1 0 10.2 5.1 5.1 0 0 1 0-10.2zm0 8.4a3.3 3.3 0 1 0 0-6.6 3.3 3.3 0 0 0 0 6.6zm6.5-8.6a1.2 1.2 0 1 1-2.4 0 1.2 1.2 0 0 1 2.4 0z"/></svg>,
};

const enc = encodeURIComponent;

const ShareButton = ({ url, title = '', text = '', compact = false, className = '', label = 'Share', testid = 'share-button' }) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const shareText = text || title;

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) });
  }, [open]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); toast({ title: 'Link copied' }); setTimeout(() => { setCopied(false); setOpen(false); }, 900); }
    catch { toast({ title: 'Copy failed' }); }
  };

  const isMobile = () => {
    if (typeof navigator === 'undefined') return false;
    const ua = /Android|iPhone|iPad|iPod|Mobile|Windows Phone|BlackBerry|IEMobile/i.test(navigator.userAgent || '');
    const coarse = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    return ua || (coarse && window.innerWidth < 768);
  };

  const trigger = async (e) => {
    e.preventDefault(); e.stopPropagation();
    // Native OS share sheet only on mobile/touch; desktop always gets the web popover.
    if (navigator.share && isMobile()) {
      try { await navigator.share({ title, text: shareText, url: shareUrl }); return; } catch { /* cancelled */ }
    }
    setOpen((o) => !o);
  };

  const targets = [
    { key: 'whatsapp', label: 'WhatsApp', href: `https://wa.me/?text=${enc(`${shareText} ${shareUrl}`)}`, cls: 'text-[#25D366]' },
    { key: 'facebook', label: 'Facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`, cls: 'text-[#1877F2]' },
    { key: 'x', label: 'X', href: `https://twitter.com/intent/tweet?text=${enc(shareText)}&url=${enc(shareUrl)}`, cls: 'text-black' },
    { key: 'telegram', label: 'Telegram', href: `https://t.me/share/url?url=${enc(shareUrl)}&text=${enc(shareText)}`, cls: 'text-[#26A5E4]' },
  ];

  const openWin = (href) => { window.open(href, '_blank', 'noopener,width=600,height=600'); setOpen(false); };
  const email = () => { window.location.href = `mailto:?subject=${enc(title)}&body=${enc(`${shareText}\n\n${shareUrl}`)}`; setOpen(false); };
  const instagram = async () => { await copy(); toast({ title: 'Link copied for Instagram', description: 'Open Instagram and paste it in your story or bio.' }); setOpen(false); };

  return (
    <div className={`relative inline-block ${className}`} ref={ref}>
      <button ref={btnRef} onClick={trigger} data-testid={testid}
        className={compact
          ? 'w-9 h-9 rounded-full bg-white/80 border border-rose/40 flex items-center justify-center text-mahogany hover:bg-rose-soft/50 transition-colors'
          : 'pill-btn-outline'}>
        <Share2 className="w-4 h-4" /> {!compact && label}
      </button>

      {open && pos && createPortal(
        <div ref={menuRef} style={{ position: 'fixed', top: pos.top, right: pos.right, zIndex: 9999 }}
          className="w-56 p-2 rounded-2xl bg-white border border-rose/50 shadow-xl" data-testid="share-popover">
          {targets.map((t) => (
            <button key={t.key} onClick={() => openWin(t.href)} data-testid={`share-${t.key}`}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-burgundy hover:bg-rose-soft/40">
              <span className={t.cls}>{Icon[t.key]({ className: 'w-5 h-5' })}</span> {t.label}
            </button>
          ))}
          <button onClick={instagram} data-testid="share-instagram" className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-burgundy hover:bg-rose-soft/40">
            <span className="text-[#E4405F]">{Icon.instagram({ className: 'w-5 h-5' })}</span> Instagram
          </button>
          <button onClick={email} data-testid="share-email"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-burgundy hover:bg-rose-soft/40">
            <Mail className="w-5 h-5 text-mahogany/70" /> Email
          </button>
          <button onClick={copy} data-testid="share-copy" className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-burgundy hover:bg-rose-soft/40 border-t border-rose/30 mt-1 pt-2">
            {copied ? <Check className="w-5 h-5 text-green-600" /> : <LinkIcon className="w-5 h-5 text-mahogany/70" />} {copied ? 'Copied!' : 'Copy link'}
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default ShareButton;
